import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import {
  TrendingUp,
  BatteryFull,
  FlashOn,
  Flag,
  FiberManualRecord,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";

const FEATURE_DATAS = [
  {
    id: 1,
    icon: TrendingUp,
    title: "Economical",
    description: "Our robots strive to make an ROI within 6-12 months",
    gradient: ["#34d399", "#10b981"],
    iconBg: "rgba(52, 211, 153, 0.15)",
  },
  {
    id: 2,
    icon: BatteryFull,
    title: "Full Day Battery Life",
    description: "Uninterrupted performance full day",
    gradient: ["#60a5fa", "#a78bfa"],
    iconBg: "rgba(96, 165, 250, 0.15)",
  },
  {
    id: 3,
    icon: FlashOn,  
    title: "High Torque",
    description: "500-1000 Kg Payload, 3 Tons Towing Capacity",
    gradient: ["#c084fc", "#f472b6"],
    iconBg: "rgba(192, 132, 252, 0.15)",
  },
  {
    id: 4,
    icon: Flag,
    title: "Made in India",
    description: "All our products are manufactured and developed in India for global market",
    gradient: ["#fb923c", "#fbbf24"],
    iconBg: "rgba(251, 146, 60, 0.15)",
  },
];

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  paddingTop: "40px",
  paddingBottom: "40px",
  [theme.breakpoints.down('md')]: {
    paddingTop: "30px",
    paddingBottom: "30px",
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: "24px",
    paddingBottom: "24px",
  },
}));

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: "absolute",
  width: 500,
  height: 500,
  borderRadius: "50%",
  background: color,
  filter: "blur(100px)",
  pointerEvents: "none",
  top,
  left,
  bottom,
  right,
}));

const FeatureCard = styled(Card)(({ theme, gradient }) => ({
  height: "100%",
  background: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(20px)",
  borderRadius: 16,
  [theme.breakpoints.down('sm')]: {
    borderRadius: 12,
  },
  border: "1px solid rgba(193, 227, 40, 0.3)",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: "rgba(71, 85, 105, 0.6)",
    boxShadow: `0 8px 20px ${gradient[0]}40`,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
    opacity: 0,
    transition: "opacity 0.5s ease",
  },
  "&:hover::before": {
    opacity: 0.02,
  },
}));

const IconContainer = styled(Box)(({ theme, gradient, iconbg }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 60,
  height: 60,
  borderRadius: 14,
  [theme.breakpoints.down('sm')]: {
    width: 50,
    height: 50,
    borderRadius: 12,
  },
  background: `linear-gradient(135deg, ${gradient[0]}18 0%, ${gradient[1]}08 100%)`,
  border: `1px solid ${gradient[0]}40`,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: `0 4px 20px ${gradient[0]}20, inset 0 1px 0 rgba(255, 255, 255, 0.1)`,
  ".MuiCard-root:hover &": {
    transform: "scale(1.05)",
    borderColor: `${gradient[0]}60`,
    boxShadow: `0 4px 16px ${gradient[0]}30, inset 0 1px 0 rgba(255, 255, 255, 0.15)`,
    background: `linear-gradient(135deg, ${gradient[0]}22 0%, ${gradient[1]}12 100%)`,
  },
}));

const GradientText = styled(Typography)(({ gradient }) => ({
  background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 700,
}));

const CornerAccent = styled(Box)(({ gradient }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  width: 128,
  height: 128,
  background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
  opacity: 0,
  filter: "blur(60px)",
  transition: "opacity 0.5s ease",
  pointerEvents: "none",
  ".MuiCard-root:hover &": {
    opacity: 0.1,
  },
}));

const FeatureCardComponent = ({ feature, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = feature.icon;

  return (
    <Box
      sx={{
        animation: `${fadeInUp} 0.6s ease-out ${index * 0.1}s both`,
        height: "100%",
      }}
    >
      <FeatureCard
        gradient={feature.gradient}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        elevation={0}
      >
        {/* Hover Gradient Overlay */}
        <CornerAccent gradient={feature.gradient} />

        {/* Card Content */}
        <CardContent
          sx={{
            p: { xs: 2.5, sm: 3 },
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            alignItems: "center",
          }}
        >
          {/* Icon Section */}
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <IconContainer gradient={feature.gradient} iconbg={feature.iconBg}>
              <Icon
                sx={{
                  fontSize: { xs: 28, sm: 32 },
                  color: feature.gradient[0],
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  filter: isHovered 
                    ? `drop-shadow(0 0 12px ${feature.gradient[0]})` 
                    : `drop-shadow(0 2px 8px ${feature.gradient[0]}60)`,
                  transform: isHovered ? "scale(1.08)" : "scale(1)",
                }}
              />
            </IconContainer>
          </Box>

          {/* Text Content */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", textAlign: "center" }}>
            <GradientText
              variant="h6"
              gradient={feature.gradient}
              sx={{ mb: { xs: 1, sm: 1.5 }, fontSize: { xs: "1rem", sm: "1.1rem" }, textAlign: "center" }}
            >
              {feature.title}
            </GradientText>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(148, 163, 184, 1)",
                lineHeight: 1.6,
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                textAlign: "center",
                transition: "color 0.3s ease",
                ".MuiCard-root:hover &": {
                  color: "rgba(203, 213, 225, 1)",
                },
              }}
            >
              {feature.description}
            </Typography>
          </Box>
        </CardContent>
      </FeatureCard>
    </Box>
  );
};

const HomepageFeature = () => {
  return (
    <BackgroundBox>
      {/* Background Orbs */}
      <GradientOrb top="25%" left={-256} color="rgba(193, 227, 40, 0.1)" />
      <GradientOrb bottom="25%" right={-256} color="rgba(193, 227, 40, 0.1)" />

      <Container maxWidth="xl" sx={{ position: "relative", px: { xs: 2, sm: 3 } }}>
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 4, sm: 6, md: 8 }}>
          {/* Badge */}
          <Chip
            icon={
              <FiberManualRecord
                sx={{
                  fontSize: 8,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
            }
            label="KEY FEATURES"
            sx={{
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(193, 227, 40, 0.3)",
              color: "#c1e328",
              fontWeight: 700,
              letterSpacing: { xs: 2, sm: 3.2 },
              fontSize: { xs: "0.5rem", sm: "0.555rem" },
              mb: { xs: 2, sm: 3 },
            }}
          />

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              fontWeight: 700,
              color: "#fff",
              letterSpacing: -1,
              mb: 2,
            }}
          >
            Why Choose
            <br />
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(135deg, #c1e328 0%, #b5d028 50%, #a8c728 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              AUTOMIST500x
            </Box>
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: "rgba(148, 163, 184, 1)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            Engineered for excellence with cutting-edge technology and unmatched
            performance
          </Typography>
        </Box>

        {/* Features Grid */}
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" sx={{ maxWidth: 'lg', width: '100%' }}>
            {FEATURE_DATAS.map((feature, index) => (
              <Grid item xs={12} sm={6} md={6} lg={3} key={feature.id} sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'stretch', sm: 'center' },
                width: { xs: '100%', sm: 'auto' }
              }}>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <FeatureCardComponent feature={feature} index={index} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bottom Stats Bar */}
        <Box
          sx={{
            mt: { xs: 4, sm: 6, md: 8 },
            p: { xs: 2, sm: 3 },
            px: { xs: 3, sm: 6, md: 10 },
            borderRadius: { xs: 3, sm: 4 },
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(193, 227, 40, 0.3)",
            width: { xs: "100%", sm: "fit-content" },
            mx: "auto",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 3, sm: 4 }}
            justifyContent="center"
            alignItems="center"
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  borderColor: "rgba(193, 227, 40, 0.3)",
                  display: { xs: "none", sm: "block" },
                }}
              />
            }
          >
            <Box textAlign="center">
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  background: "linear-gradient(135deg, #e4ff3a 0%, #c1e328 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                6-12
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(148, 163, 184, 1)", mt: 0.5, display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                Months ROI
              </Typography>
            </Box>

            <Box textAlign="center">
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  background: "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                500-1000 Kg
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(148, 163, 184, 1)", mt: 0.5, display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                Payload
              </Typography>
            </Box>

            <Box textAlign="center">
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  background: "linear-gradient(135deg, #c1e328 0%, #8ab82e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                3 Tons
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(148, 163, 184, 1)", mt: 0.5, display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                Towing Capacity
              </Typography>
            </Box>

            <Box textAlign="center">
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  background: "linear-gradient(135deg, #b8d92e 0%, #9dc82e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                100%
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(148, 163, 184, 1)", mt: 0.5, display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                Made in India
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Container>
    </BackgroundBox>
  );
};

export default HomepageFeature;